import React, { useCallback, useEffect, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { api } from '../lib/api';

type SyncRun = Awaited<ReturnType<typeof api.getWbsSyncRuns>>[number];

interface Props {
    projectName?: string;
    onSyncComplete?: () => void;
    /**
     * When true, render an icon-first pill that fits comfortably in the app
     * header next to the other action buttons. The verbose "last sync" line
     * becomes a hover tooltip instead of inline text. Default: true.
     */
    compact?: boolean;
}

function formatShortTimestamp(iso: string): string {
    const d = new Date(iso);
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const hh = String(d.getHours()).padStart(2, '0');
    const mi = String(d.getMinutes()).padStart(2, '0');
    return `${dd}/${mm} ${hh}:${mi}`;
}

export const WbsSyncControl: React.FC<Props> = ({ projectName, onSyncComplete, compact = true }) => {
    const [runs, setRuns] = useState<SyncRun[]>([]);
    const [busy, setBusy] = useState(false);
    const [lastResult, setLastResult] = useState<string | null>(null);

    const loadRuns = useCallback(async () => {
        try {
            const rows = await api.getWbsSyncRuns(5);
            setRuns(rows);
        } catch {
            /* swallow — non-critical */
        }
    }, []);

    useEffect(() => { loadRuns(); }, [loadRuns]);

    const doSync = async () => {
        setBusy(true);
        setLastResult(null);
        try {
            if (projectName) {
                const r = await api.syncWbsProject(projectName);
                setLastResult(
                    `${r.project}: ${r.rows_in} rows (${r.inserted} new, ${r.updated} updated, ${r.unchanged} unchanged) from ${r.source}`
                );
            } else {
                const r = await api.syncAllWbsProjects();
                setLastResult(JSON.stringify(r));
            }
            await loadRuns();
            onSyncComplete?.();
        } catch (err: any) {
            setLastResult(`Error: ${err?.message || 'sync failed'}`);
        } finally {
            setBusy(false);
        }
    };

    const lastSuccess = runs.find(r => !r.error && r.finished_at);

    // Human-readable hover tooltip for the compact variant. Kept verbose so the
    // operator can see the same detail the old inline label used to show.
    const tooltip = (() => {
        const parts: string[] = [];
        if (lastSuccess) {
            parts.push(
                `Last sync: ${new Date(lastSuccess.started_at).toLocaleString()} (${lastSuccess.source})`
            );
            if (lastSuccess.rows_in != null) parts.push(`${lastSuccess.rows_in} rows`);
        } else {
            parts.push('No successful WBS sync yet');
        }
        if (lastResult) parts.push(lastResult);
        return parts.join(' · ');
    })();

    const buttonLabel = busy
        ? 'Syncing…'
        : projectName ? `Sync ${projectName}` : 'Sync all';

    if (compact) {
        return (
            <div
                className="inline-flex items-center gap-2"
                title={tooltip}
            >
                <button
                    type="button"
                    onClick={doSync}
                    disabled={busy}
                    className="inline-flex items-center gap-1.5 rounded-md bg-slate-800 px-2.5 py-1.5 text-xs font-semibold text-slate-100 border border-slate-600 hover:bg-slate-700 disabled:opacity-60"
                >
                    <RefreshCw className={`w-3.5 h-3.5 ${busy ? 'animate-spin' : ''}`} />
                    {buttonLabel}
                </button>
                {/* Small dim timestamp so the operator has at-a-glance freshness info
                    without the control stealing header real estate. */}
                {lastSuccess && (
                    <span className="text-[11px] text-gray-400 dark:text-slate-500 whitespace-nowrap hidden md:inline">
                        {formatShortTimestamp(lastSuccess.started_at)}
                    </span>
                )}
                {lastResult && lastResult.startsWith('Error') && (
                    <span className="text-[11px] text-red-600" title={lastResult}>!</span>
                )}
            </div>
        );
    }

    return (
        <div className="inline-flex items-center gap-3 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm shadow-sm dark:border-slate-700 dark:bg-slate-800">
            <div className="flex flex-col">
                <span className="text-xs text-gray-500 dark:text-slate-400">WBS Source</span>
                {lastSuccess ? (
                    <span className="text-xs text-gray-700 dark:text-slate-200">
                        Last: {new Date(lastSuccess.started_at).toLocaleString()} · {lastSuccess.source}
                        {lastSuccess.rows_in != null && ` · ${lastSuccess.rows_in} rows`}
                    </span>
                ) : (
                    <span className="text-xs text-gray-400">No successful sync yet</span>
                )}
            </div>
            <button
                type="button"
                onClick={doSync}
                disabled={busy}
                className="rounded-md bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
            >
                {buttonLabel}
            </button>
            {lastResult && (
                <span
                    className={`text-xs ${lastResult.startsWith('Error') ? 'text-red-600' : 'text-green-700 dark:text-green-400'}`}
                    title={lastResult}
                >
                    {lastResult.length > 80 ? lastResult.slice(0, 77) + '…' : lastResult}
                </span>
            )}
        </div>
    );
};
